import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import type { User as SupabaseUser, Session } from '@supabase/supabase-js'
import { isPasswordValid } from '../utils/passwordStrength'

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  createdAt: string
  subscriptionStatus: 'freemium' | 'professional' | 'studio' | null
  subscriptionId: string | null
  role: 'user' | 'admin' | 'super_admin'
  profilePicture?: string
  businessName?: string
  businessNames?: string[]
  phone?: string
  timezone?: string
  notificationPreferences?: {
    email: boolean
    sms: boolean
    push: boolean
    marketing: boolean
  }
  bio?: string
  website?: string
  socialLinks?: {
    instagram?: string
    facebook?: string
    twitter?: string
    linkedin?: string
  }
}

interface AuthContextType {
  user: User | null
  session: Session | null
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>
  signup: (firstName: string, lastName: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  isLoading: boolean
  showSessionWarning: boolean
  extendSession: () => void
  isRemembered: boolean
  isAdmin: () => boolean
  isSuperAdmin: () => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null)
  const [showSessionWarning, setShowSessionWarning] = useState(false)
  const [activityTimeout, setActivityTimeout] = useState<NodeJS.Timeout | null>(null)
  const [warningTimeout, setWarningTimeout] = useState<NodeJS.Timeout | null>(null)
  const [isRemembered, setIsRemembered] = useState(false)

  // Cache user data to prevent reverting to freemium on temporary errors
  const getCachedUser = (userId: string): User | null => {
    try {
      const cached = localStorage.getItem(`shootsuite_user_${userId}`)
      if (cached) {
        const userData = JSON.parse(cached)
        // Extend cache validity to 15 minutes for better reliability
        const cacheAge = Date.now() - userData.cachedAt
        if (cacheAge < 15 * 60 * 1000) { // 15 minutes instead of 5
          console.log('Using cached user data (age:', Math.round(cacheAge / 1000 / 60), 'minutes)')
          return userData.user
        } else {
          console.log('Cached user data expired (age:', Math.round(cacheAge / 1000 / 60), 'minutes)')
        }
      }
    } catch (error) {
      console.warn('Error reading cached user data:', error)
    }
    return null
  }

  const setCachedUser = (user: User) => {
    try {
      const cacheData = {
        user,
        cachedAt: Date.now()
      }
      localStorage.setItem(`shootsuite_user_${user.id}`, JSON.stringify(cacheData))
      console.log('User data cached successfully for:', user.email)
    } catch (error) {
      console.warn('Error caching user data:', error)
    }
  }

  // Enhanced cache management with backup storage
  const getBackupCachedUser = (userId: string): User | null => {
    try {
      const backupKey = `shootsuite_user_backup_${userId}`
      const cached = localStorage.getItem(backupKey)
      if (cached) {
        const userData = JSON.parse(cached)
        // Backup cache is valid for 24 hours
        const cacheAge = Date.now() - userData.cachedAt
        if (cacheAge < 24 * 60 * 60 * 1000) { // 24 hours
          console.log('Using backup cached user data (age:', Math.round(cacheAge / 1000 / 60 / 60), 'hours)')
          return userData.user
        }
      }
    } catch (error) {
      console.warn('Error reading backup cached user data:', error)
    }
    return null
  }

  const setBackupCachedUser = (user: User) => {
    try {
      // Only backup if user has premium subscription
      if (user.subscriptionStatus && user.subscriptionStatus !== 'freemium') {
        const backupKey = `shootsuite_user_backup_${user.id}`
        const cacheData = {
          user,
          cachedAt: Date.now()
        }
        localStorage.setItem(backupKey, JSON.stringify(cacheData))
        console.log('Premium user data backed up for:', user.email)
      }
    } catch (error) {
      console.warn('Error backing up user data:', error)
    }
  }

  const clearCachedUser = (userId: string) => {
    try {
      localStorage.removeItem(`shootsuite_user_${userId}`)
      localStorage.removeItem(`shootsuite_user_backup_${userId}`)
      console.log('Cleared cached user data for:', userId)
    } catch (error) {
      console.warn('Error clearing cached user data:', error)
    }
  }

  // Helper function to clear loading immediately (for successful operations)
  const clearLoadingImmediately = () => {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      setLoadingTimeout(null)
    }
    setIsLoading(false)
  }

  // Activity tracking constants
  const INACTIVITY_WARNING_TIME = 4 * 60 * 1000 // 4 minutes
  const WARNING_DURATION = 60 * 1000 // 1 minute to respond

  // Update last activity time
  const updateActivity = () => {
    // Only track activity if user is logged in and not showing warning
    if (!user || showSessionWarning) {
      return
    }
    
    // Clear existing timeouts
    if (activityTimeout) {
      clearTimeout(activityTimeout)
      setActivityTimeout(null)
    }
    if (warningTimeout) {
      clearTimeout(warningTimeout)
      setWarningTimeout(null)
    }
    
    // Set new warning timeout (4 minutes)
    const newActivityTimeout = setTimeout(() => {
      // Double-check user is still logged in before showing warning
      if (user) {
        setShowSessionWarning(true)
        
        // Set timeout to actually log out (1 minute after warning)
        const newWarningTimeout = setTimeout(() => {
          logout()
        }, WARNING_DURATION)
        
        setWarningTimeout(newWarningTimeout)
      }
    }, INACTIVITY_WARNING_TIME)
    
    setActivityTimeout(newActivityTimeout)
  }

  // Extend session when user confirms they're still there
  const extendSession = () => {
    // Clear the warning state first
    setShowSessionWarning(false)
    
    // Clear all existing timeouts
    if (activityTimeout) {
      clearTimeout(activityTimeout)
      setActivityTimeout(null)
    }
    if (warningTimeout) {
      clearTimeout(warningTimeout)
      setWarningTimeout(null)
    }
    
    // Only restart activity tracking if user is still logged in
    if (user) {
      // Start fresh activity tracking
      const newActivityTimeout = setTimeout(() => {
        setShowSessionWarning(true)
        
        // Set timeout to actually log out (1 minute after warning)
        const newWarningTimeout = setTimeout(() => {
          logout()
        }, WARNING_DURATION)
        
        setWarningTimeout(newWarningTimeout)
      }, INACTIVITY_WARNING_TIME)
      
      setActivityTimeout(newActivityTimeout)
    }
  }

  // Setup activity listeners
  const setupActivityListeners = () => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    
    const handleActivity = () => {
      // Only track activity if user is logged in and warning is not showing
      // User must explicitly click "I'm Still Here" to extend session when warning is shown
      if (!user || showSessionWarning) {
        return
      }
      
      // Normal activity tracking when no warning is shown
      updateActivity()
    }

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    // Handle page visibility changes (tab switching)
    const handleVisibilityChange = () => {
      // Only track tab focus changes when user is logged in and no warning is showing
      if (!document.hidden && user && !showSessionWarning) {
        updateActivity()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Initial activity setup only if user is logged in
    if (user) {
      updateActivity()
    }

    // Return cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      
      if (activityTimeout) clearTimeout(activityTimeout)
      if (warningTimeout) clearTimeout(warningTimeout)
    }
  }

  // Convert Supabase user to our User type
  const convertSupabaseUser = async (supabaseUser: SupabaseUser): Promise<User | null> => {
    try {
      // Check if Supabase is configured
      if (!supabase) {
        console.error('Supabase not configured')
        return null
      }

      console.log('Converting user:', supabaseUser.id, supabaseUser.email)
      
      // Create a timeout promise to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), 10000) // Increased to 10 seconds
      })
      
      // Try to get user profile with timeout (force fresh data)
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', supabaseUser.id)
        .single()
      
      const { data: profile, error } = await Promise.race([profilePromise, timeoutPromise]) as any

      console.log('Profile query result:', { profile, error })

      if (error) {
        console.error('Error fetching user profile:', error)
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        })
        
        // Enhanced fallback strategy: try multiple cache sources
        console.log('Profile fetch failed, trying cached data...')
        
        // First, try regular cache
        let cachedUser = getCachedUser(supabaseUser.id)
        if (cachedUser) {
          console.log('Using regular cached user data:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
          return cachedUser
        }
        
        // Second, try backup cache (for premium users)
        cachedUser = getBackupCachedUser(supabaseUser.id)
        if (cachedUser) {
          console.log('Using backup cached user data:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
          // Update regular cache with backup data
          setCachedUser(cachedUser)
          return cachedUser
        }
        
        // If no cached data and error is NOT "not found", try to create profile
        if (error.code !== 'PGRST116') { // PGRST116 is "not found"
          console.log('Profile fetch failed with non-404 error, attempting to create profile...')
          try {
            const { error: insertError } = await supabase
              .from('profiles')
              .insert({
                id: supabaseUser.id,
                first_name: supabaseUser.user_metadata?.first_name || 'User',
                last_name: supabaseUser.user_metadata?.last_name || '',
                email: supabaseUser.email || '',
                subscription_status: 'freemium'
              })
            
            if (insertError) {
              console.warn('Could not create profile:', insertError)
            } else {
              console.log('Profile created successfully, retrying fetch...')
              // Retry the profile fetch after creation with shorter timeout
              const retryPromise = supabase
                .from('profiles')
                .select('*')
                .eq('id', supabaseUser.id)
                .single()
              
              const retryTimeout = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Retry timeout')), 5000)
              })
              
              const { data: newProfile, error: retryError } = await Promise.race([retryPromise, retryTimeout]) as any
              
              if (!retryError && newProfile) {
                const user = {
                  id: newProfile.id,
                  firstName: newProfile.first_name,
                  lastName: newProfile.last_name,
                  email: newProfile.email,
                  createdAt: newProfile.created_at,
                  subscriptionStatus: newProfile.subscription_status,
                  subscriptionId: newProfile.subscription_id,
                  role: newProfile.role || 'user',
                  profilePicture: newProfile.profile_picture,
                  businessName: newProfile.business_name,
                  businessNames: newProfile.business_names,
                  phone: newProfile.phone,
                  timezone: newProfile.timezone,
                  notificationPreferences: newProfile.notification_preferences,
                  bio: newProfile.bio,
                  website: newProfile.website,
                  socialLinks: newProfile.social_links
                }
                setCachedUser(user)
                setBackupCachedUser(user)
                return user
              }
            }
          } catch (insertError) {
            console.warn('Profile creation failed:', insertError)
          }
        }
        
        // Last resort: create minimal fallback user but preserve any cached subscription info
        console.log('All profile operations failed, creating fallback user...')
        const fallbackUser: User = {
          id: supabaseUser.id,
          firstName: supabaseUser.user_metadata?.first_name || supabaseUser.email?.split('@')[0] || 'User',
          lastName: supabaseUser.user_metadata?.last_name || '',
          email: supabaseUser.email || '',
          createdAt: supabaseUser.created_at,
          subscriptionStatus: 'freemium',
          subscriptionId: null,
          role: 'user'
        }
        
        // Try to preserve subscription status from any available cache
        const anyCache = getCachedUser(supabaseUser.id) || getBackupCachedUser(supabaseUser.id)
        if (anyCache && anyCache.subscriptionStatus && anyCache.subscriptionStatus !== 'freemium') {
          console.log('Preserving subscription status from cache:', anyCache.subscriptionStatus)
          fallbackUser.subscriptionStatus = anyCache.subscriptionStatus
          fallbackUser.subscriptionId = anyCache.subscriptionId
        }
        
        console.log('Returning fallback user:', fallbackUser)
        return fallbackUser
      }

      // Successfully got profile data - cache it and return
      const user = {
        id: profile.id,
        firstName: profile.first_name,
        lastName: profile.last_name,
        email: profile.email,
        createdAt: profile.created_at,
        subscriptionStatus: profile.subscription_status,
        subscriptionId: profile.subscription_id,
        role: profile.role || 'user',
        profilePicture: profile.profile_picture,
        businessName: profile.business_name,
        businessNames: profile.business_names,
        phone: profile.phone,
        timezone: profile.timezone,
        notificationPreferences: profile.notification_preferences,
        bio: profile.bio,
        website: profile.website,
        socialLinks: profile.social_links
      }
      
      // Cache the successful profile data
      setCachedUser(user)
      setBackupCachedUser(user)
      console.log('Profile fetched successfully and cached:', user.email, 'subscription:', user.subscriptionStatus)
      return user
    } catch (error) {
      console.error('Error converting user:', error)
      
      // Enhanced error recovery with multiple cache sources
      console.log('Conversion error, trying cached data...')
      
      // Try regular cache first
      let cachedUser = getCachedUser(supabaseUser.id)
      if (cachedUser) {
        console.log('Using cached user data due to conversion error:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
        return cachedUser
      }
      
      // Try backup cache
      cachedUser = getBackupCachedUser(supabaseUser.id)
      if (cachedUser) {
        console.log('Using backup cached user data due to conversion error:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
        // Update regular cache
        setCachedUser(cachedUser)
        return cachedUser
      }
      
      // Final fallback: create user from auth data if profile system fails completely
      console.log('Using final fallback user creation from auth data')
      const fallbackUser: User = {
        id: supabaseUser.id,
        firstName: supabaseUser.user_metadata?.first_name || supabaseUser.email?.split('@')[0] || 'User',
        lastName: supabaseUser.user_metadata?.last_name || '',
        email: supabaseUser.email || '',
        createdAt: supabaseUser.created_at,
        subscriptionStatus: 'freemium',
        subscriptionId: null,
        role: 'user'
      }
      console.log('Returning final fallback user:', fallbackUser)
      return fallbackUser
    }
  }

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if Supabase is configured
        if (!supabase) {
          console.warn('Supabase not configured, skipping auth initialization')
          setIsLoading(false)
          return
        }

        // Check for remember me preference
        const rememberMeStored = localStorage.getItem('shootsuite_remember_me') === 'true'
        setIsRemembered(rememberMeStored)

        // Get initial session
        const { data: { session: initialSession } } = await supabase.auth.getSession()
        
        if (initialSession?.user) {
          setSession(initialSession)
          const convertedUser = await convertSupabaseUser(initialSession.user)
          setUser(convertedUser)
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
      } finally {
        clearLoadingImmediately()
      }
    }

    initializeAuth()

    // Setup activity tracking only if user is logged in
    let cleanupActivity: (() => void) | null = null
    
    // Setup periodic user data refresh (every 2 minutes) to keep cache fresh
    let refreshInterval: NodeJS.Timeout | null = null
    
    // Listen for auth changes only if Supabase is configured
    if (supabase) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('Auth state change:', event, session?.user?.email)
        
        // Always update session first
        setSession(session)
        
        if (session?.user) {
          console.log('Processing user session for:', session.user.email)
          
          try {
            const convertedUser = await convertSupabaseUser(session.user)
            console.log('Setting user:', convertedUser)
            setUser(convertedUser)
            
            // Setup activity tracking for logged-in users
            if (!cleanupActivity) {
              cleanupActivity = setupActivityListeners()
            }
            
            // Setup periodic refresh for logged-in users
            if (!refreshInterval) {
              refreshInterval = setInterval(async () => {
                try {
                  console.log('Periodic user data refresh...')
                  await refreshUser()
                } catch (error) {
                  console.warn('Periodic refresh failed:', error)
                }
              }, 2 * 60 * 1000) // Every 2 minutes
            }
          } catch (error) {
            console.error('Error in auth state change:', error)
            
            // Enhanced error recovery - try to preserve user data from cache
            console.log('Auth state change error, attempting recovery...')
            
            // Try to get cached user data first
            let cachedUser = getCachedUser(session.user.id)
            if (cachedUser) {
              console.log('Recovered user from cache:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
              setUser(cachedUser)
              
              // Setup activity tracking for recovered users
              if (!cleanupActivity) {
                cleanupActivity = setupActivityListeners()
              }
              return
            }
            
            // Try backup cache
            cachedUser = getBackupCachedUser(session.user.id)
            if (cachedUser) {
              console.log('Recovered user from backup cache:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
              setUser(cachedUser)
              // Update regular cache
              setCachedUser(cachedUser)
              
              // Setup activity tracking for recovered users
              if (!cleanupActivity) {
                cleanupActivity = setupActivityListeners()
              }
              return
            }
            
            // Last resort: create basic user but try to preserve subscription info
            console.log('Creating fallback user for auth state change error')
            const fallbackUser: User = {
              id: session.user.id,
              firstName: session.user.email?.split('@')[0] || 'User',
              lastName: '',
              email: session.user.email || '',
              createdAt: session.user.created_at,
              subscriptionStatus: 'freemium',
              subscriptionId: null,
              role: 'user'
            }
            
            // Try to preserve subscription from any available cache
            const anyCache = getCachedUser(session.user.id) || getBackupCachedUser(session.user.id)
            if (anyCache && anyCache.subscriptionStatus && anyCache.subscriptionStatus !== 'freemium') {
              console.log('Preserving subscription in fallback user:', anyCache.subscriptionStatus)
              fallbackUser.subscriptionStatus = anyCache.subscriptionStatus
              fallbackUser.subscriptionId = anyCache.subscriptionId
            }
            
            setUser(fallbackUser)
            
            // Setup activity tracking even for fallback users
            if (!cleanupActivity) {
              cleanupActivity = setupActivityListeners()
            }
          }
        } else {
          console.log('No session, clearing user')
          setUser(null)
          
          // Clear session warning state when logged out
          setShowSessionWarning(false)
          
          // Cleanup activity tracking when logged out
          if (cleanupActivity) {
            cleanupActivity()
            cleanupActivity = null
          }
          
          // Clear periodic refresh when logged out
          if (refreshInterval) {
            clearInterval(refreshInterval)
            refreshInterval = null
          }
        }
        
        clearLoadingImmediately()
      })

      return () => {
        subscription.unsubscribe()
        
        // Cleanup activity tracking
        if (cleanupActivity) {
          cleanupActivity()
        }
        
        // Clear periodic refresh
        if (refreshInterval) {
          clearInterval(refreshInterval)
        }
        
        // Clean up any pending timeouts
        if (loadingTimeout) clearTimeout(loadingTimeout)
        if (activityTimeout) clearTimeout(activityTimeout)
        if (warningTimeout) clearTimeout(warningTimeout)
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (loadingTimeout) clearTimeout(loadingTimeout)
      if (activityTimeout) clearTimeout(activityTimeout)
      if (warningTimeout) clearTimeout(warningTimeout)
    }
  }, [])

  const signup = async (firstName: string, lastName: string, email: string, password: string) => {
    try {
      setIsLoading(true) // Immediate loading for user-initiated actions
      
      // Check if Supabase is properly configured
      if (!supabase) {
        return {
          success: false,
          error: 'Authentication service is not configured. Please check your environment variables.'
        }
      }
      
      // Validate password strength
      if (!isPasswordValid(password)) {
        return {
          success: false,
          error: 'Password must meet all security requirements: at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character.'
        }
      }

      // Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase!.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            role: 'user' // Set default role to user
          },
          emailRedirectTo: `${window.location.origin}/auth`
        }
      })

      if (authError) {
        return {
          success: false,
          error: authError.message
        }
      }

      if (!authData.user) {
        return {
          success: false,
          error: 'Failed to create user account'
        }
      }

      // Create user profile in our profiles table
      const { error: profileError } = await supabase!
        .from('profiles')
        .insert({
          id: authData.user.id,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.toLowerCase().trim(),
          subscription_status: 'freemium', // Default to freemium plan
          role: 'user' // Set role to user
        })

      if (profileError) {
        console.error('Error creating profile:', profileError)
        // Note: User is still created in auth, but profile creation failed
        // In production, you might want to handle this differently
      }

      // Create client record in clients table
      const { error: clientError } = await supabase!
        .from('clients')
        .insert({
          user_id: authData.user.id,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.toLowerCase().trim(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })

      if (clientError) {
        console.error('Error creating client record:', clientError)
        // Note: User and profile are created, but client record creation failed
        // In production, you might want to handle this differently
      }

      return { success: true }
    } catch (error) {
      console.error('Signup error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred during signup'
      }
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      setIsLoading(true) // Immediate loading for user-initiated actions
      
      // Check if Supabase is properly configured
      if (!supabase) {
        return {
          success: false,
          error: 'Authentication service is not configured. Please check your environment variables.'
        }
      }
      
      const { data, error } = await supabase!.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password
      })

      console.log('Login response:', { data, error })

      if (error) {
        console.error('Login error details:', error)
        return {
          success: false,
          error: error.message
        }
      }

      if (!data.user) {
        return {
          success: false,
          error: 'Login failed - no user returned'
        }
      }

      if (!data.session) {
        return {
          success: false,
          error: 'Login failed - no session created. Please check your email for verification.'
        }
      }

      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('shootsuite_remember_me', 'true')
        setIsRemembered(true)
      } else {
        localStorage.removeItem('shootsuite_remember_me')
        setIsRemembered(false)
      }

      console.log('Login successful:', { 
        userId: data.user.id, 
        email: data.user.email,
        emailConfirmed: data.user.email_confirmed_at,
        rememberMe 
      })

      return { success: true }
    } catch (error) {
      console.error('Login error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred during login'
      }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      setIsLoading(true)
      
      // Clear session warning state immediately
      setShowSessionWarning(false)
      
      // Clear all timeouts
      if (activityTimeout) {
        clearTimeout(activityTimeout)
        setActivityTimeout(null)
      }
      if (warningTimeout) {
        clearTimeout(warningTimeout)
        setWarningTimeout(null)
      }
      
      // Clear cached user data
      if (user?.id) {
        clearCachedUser(user.id)
      }
      
      // Clear remember me preference
      localStorage.removeItem('shootsuite_remember_me')
      setIsRemembered(false)
      
      if (supabase) {
        await supabase.auth.signOut()
      }
      setUser(null)
      setSession(null)
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const refreshUser = async () => {
    try {
      if (!session?.user || !supabase) {
        console.log('RefreshUser: No session or supabase')
        return
      }
      
      console.log('RefreshUser: Fetching fresh profile data...')
      
      // Create timeout for refresh operation
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Refresh timeout')), 8000) // 8 second timeout
      })
      
      // Fetch fresh profile data directly
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single()

      const { data: profile, error } = await Promise.race([profilePromise, timeoutPromise]) as any

      if (error) {
        console.error('RefreshUser: Error fetching profile:', error)
        
        // Don't clear user data on refresh error - try to use cached data instead
        console.log('RefreshUser: Attempting to use cached data due to error')
        
        // Try regular cache first
        let cachedUser = getCachedUser(session.user.id)
        if (cachedUser) {
          console.log('RefreshUser: Using cached user data:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
          setUser(cachedUser)
          return
        }
        
        // Try backup cache
        cachedUser = getBackupCachedUser(session.user.id)
        if (cachedUser) {
          console.log('RefreshUser: Using backup cached user data:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
          setUser(cachedUser)
          // Update regular cache
          setCachedUser(cachedUser)
          return
        }
        
        // If no cache available, keep existing user data unchanged
        console.log('RefreshUser: No cached data available, keeping existing user data')
        return
      }

      console.log('RefreshUser: Fresh profile data:', profile)

      // Update user with fresh data
      const updatedUser = {
        id: profile.id,
        firstName: profile.first_name,
        lastName: profile.last_name,
        email: profile.email,
        createdAt: profile.created_at,
        subscriptionStatus: profile.subscription_status,
        subscriptionId: profile.subscription_id,
        role: profile.role || 'user',
        profilePicture: profile.profile_picture || null,
        businessName: profile.business_name || null,
        businessNames: profile.business_names || [],
        phone: profile.phone || null,
        timezone: profile.timezone || 'America/New_York',
        notificationPreferences: profile.notification_preferences || {
          email: true,
          sms: false,
          push: true,
          marketing: false
        },
        bio: profile.bio || null,
        website: profile.website || null,
        socialLinks: profile.social_links || {}
      }

      console.log('RefreshUser: Setting updated user:', updatedUser.email, 'subscription:', updatedUser.subscriptionStatus)
      setUser(updatedUser)
      
      // Update cached data with fresh profile
      setCachedUser(updatedUser)
      setBackupCachedUser(updatedUser)
      console.log('RefreshUser: User state and cache updated successfully')
    } catch (error) {
      console.error('Error refreshing user:', error)
      
      // Enhanced error recovery for refresh operations
      console.log('RefreshUser: Catch block - attempting cache recovery')
      
      if (!session?.user) {
        console.log('RefreshUser: No session available in catch block')
        return
      }
      
      // Try to recover from cache
      let cachedUser = getCachedUser(session.user.id)
      if (cachedUser) {
        console.log('RefreshUser: Recovered from cache in catch block:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
        setUser(cachedUser)
        return
      }
      
      // Try backup cache
      cachedUser = getBackupCachedUser(session.user.id)
      if (cachedUser) {
        console.log('RefreshUser: Recovered from backup cache in catch block:', cachedUser.email, 'subscription:', cachedUser.subscriptionStatus)
        setUser(cachedUser)
        setCachedUser(cachedUser)
        return
      }
      
      // Don't clear user data on refresh error - keep existing data
      console.log('RefreshUser: No recovery possible, keeping existing user data unchanged')
    }
  }

  // Helper functions for role checking
  const isAdmin = () => {
    return user?.role === 'admin' || user?.role === 'super_admin'
  }

  const isSuperAdmin = () => {
    return user?.role === 'super_admin'
  }

  const value = {
    user,
    session,
    login,
    signup,
    logout,
    refreshUser,
    isLoading,
    showSessionWarning,
    extendSession,
    isRemembered,
    isAdmin,
    isSuperAdmin
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 